/*
 * Copyright (c) 2018 CalAmp Corp.  All Rights Reserved
 */
import isString from 'lodash.isstring';
import GroupProperties from '../services/GroupProperties';
import {GroupTypeEntity} from '../types/entities';

/**
 * @function lookup
 *
 * Allows for performing a look-up of a specific type within the list of available
 * group types for the given entity.
 *
 * @param {GroupTypeEntity} entity the GroupTypeEntity from which to reference.
 * @param {unknown} The type value which is to be tested.
 */
const lookup = (entity: GroupTypeEntity, type: unknown): any => {
  const definition: any = GroupProperties.getEntityTypes(entity) || {};
  return (definition.value || []).find(function(value: unknown) {
    return areTypesEqual(entity, type, value);
  });
};

/**
 * @function transform
 *
 * Allows for transforming specfic group types for entities which required transforms
 * due to CTC platform contrainsts, specifically, operators which must be lowercased.
 *
 * @param {GroupTypeEntity} the given entity to determine if transforms are required.
 * @param {string|number} type the arbitrary group types for the given entity.
 */
export const transform = (entity: GroupTypeEntity, type: any) => {
  if (isString(type) && entity === GroupTypeEntity.operator) {
    return type.toLowerCase();
  }
  return type;
};

/**
 * @function areTypesEqual
 *
 * Allows for determining if two specific group entity types are equal.
 *
 * @param {GroupTypeEntity} entity the GroupTypeEntity from which to reference.
 * @param {unknown} first the first type value which is to be tested.
 * @param {unknown} second the second type value which is to be tested.
 */
export const areTypesEqual = (entity: GroupTypeEntity, first, second) => {
  return transform(entity, first) === transform(entity, second);
};

/**
 * @function toProp
 *
 * Allows for converting a group type for a specific entity that needs to be transformed
 * specifically for the platform.
 *
 * @param {GroupTypeEntity} entity the GroupTypeEntity from which to reference.
 * @param {unknown} The type value which is to be converted.
 */
export const toProp = (entity: GroupTypeEntity, type: unknown) => {
  return transform(entity, type);
};

/**
 * @function toUI
 *
 * Allows for converting a group type for a specific entity that needs to be transformed
 * specifically for the UI.
 *
 * @param {GroupTypeEntity} entity the GroupTypeEntity from which to reference.
 * @param {unknown} The type value which is to be converted.
 */
export const toUI = (entity: GroupTypeEntity, type: unknown) => {
  return lookup(entity, type) || '';
};
